import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ArrowRight } from 'lucide-react';
import { login, startOAuthLogin } from '../services/auth';
import { usePilotFlags } from '../config/pilotFlags';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicInlineLinkSx,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [oauthLoading, setOauthLoading] = useState({ google: false, microsoft: false });
  const { hideOAuthButtons: oauthButtonsHidden } = usePilotFlags();

  useEffect(() => {
    if (location.state?.message) {
      setLoginError(location.state.message);
    }
  }, [location.state]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.email) {
      nextErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      nextErrors.email = 'Email address is invalid';
    }

    if (!formData.password) {
      nextErrors.password = 'Password is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoginError('');
    setNeedsVerification(false);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await login({ email: formData.email, password: formData.password });
      const redirectTo = location.state?.redirect || '/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.isVerified === false) {
        setNeedsVerification(true);
      } else if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_DEACTIVATED') {
        setLoginError('Your account has been deactivated. Please contact support if you believe this is an error.');
      } else {
        setLoginError(error.response?.data?.message || 'Invalid email or password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOAuth = async (provider) => {
    setLoginError('');
    setNeedsVerification(false);
    setOauthLoading((prev) => ({ ...prev, [provider]: true }));

    try {
      const result = await startOAuthLogin(provider);
      if (result.status === 'authenticated') {
        const redirectTo = location.state?.redirect || '/dashboard';
        navigate(redirectTo, { replace: true });
      } else if (result.status === 'needs_review') {
        setLoginError(result.message || 'We need additional information to complete your sign-in.');
      } else if (result.status === 'error') {
        setLoginError(result.message || `Unable to sign in with ${provider}. Please try again.`);
      }
    } catch (error) {
      setLoginError(error.message || `Unable to sign in with ${provider}. Please try again.`);
    } finally {
      setOauthLoading((prev) => ({ ...prev, [provider]: false }));
    }
  };

  return (
    <PublicPageFrame>
      <PublicCardHeader
        eyebrow="Sign in"
        title="Log in"
        description="Use your email and password to access your Virtual Medic account."
      />

      {loginError && <Alert severity="error" sx={{ mb: 3 }}>{loginError}</Alert>}
      {needsVerification && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please verify your email before logging in. Check your inbox for the verification link.
        </Alert>
      )}

      {!oauthButtonsHidden && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleOAuth('google')}
                disabled={oauthLoading.google}
                startIcon={
                  oauthLoading.google ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Box
                      component="img"
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      sx={{ width: 20, height: 20 }}
                    />
                  )
                }
                sx={{
                  ...publicSecondaryButtonSx,
                  minHeight: 50,
                  width: '100%',
                }}
              >
                {oauthLoading.google ? 'Connecting...' : 'Continue with Google'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleOAuth('microsoft')}
                disabled={oauthLoading.microsoft}
                startIcon={
                  oauthLoading.microsoft ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Box
                      component="img"
                      src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                      alt="Microsoft"
                      sx={{ width: 18, height: 18 }}
                    />
                  )
                }
                sx={{
                  ...publicSecondaryButtonSx,
                  minHeight: 50,
                  width: '100%',
                }}
              >
                {oauthLoading.microsoft ? 'Connecting...' : 'Continue with Microsoft'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      <Divider sx={{ my: 3 }}>
        <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
          {oauthButtonsHidden ? 'Log in with email' : 'Or continue with email'}
        </Typography>
      </Divider>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            required
            fullWidth
            id="email"
            label="Email address"
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
            sx={publicTextFieldSx}
          />
          <TextField
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            error={Boolean(errors.password)}
            helperText={errors.password}
            sx={publicTextFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Typography component={Link} to="/forgot-password" sx={publicInlineLinkSx}>
              Forgot password?
            </Typography>
          </Box>

          <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={publicPrimaryButtonSx}>
            {isSubmitting ? 'Logging in...' : 'Log in'}
          </Button>
        </Stack>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        sx={{ mt: 3 }}
      >
        <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
          Don't have an account yet?
        </Typography>
        <Button component={Link} to="/signup" endIcon={<ArrowRight size={16} />} sx={{ ...publicInlineLinkSx, px: 0 }}>
          Sign up
        </Button>
      </Stack>
    </PublicPageFrame>
  );
}

export default LoginPage;
